/**
 * Single source of truth for story "game time" delta.
 * Delta = accumulated time spent only on cards where the user can give an answer (hasFeature.answer).
 * Only VIEW_CARD and FINISH_STORY participate in the calculation;
 * other events still send delta via getDeltaForEvent but are not stored.
 * One value: live on game card, frozen when not on an answerable card.
 * Call getDeltaForEvent at the very start of each event thunk (before any early return).
 */

import type { CardData } from 'types/story';
import { CardFacade } from 'common/utils/facades/card-facade';
import { StoryEventType } from './types';

const w = typeof window !== 'undefined' ? window : null;
const INIT_TIME = w?.STORY_INITIAL_TIME ?? Date.now();

function isGameCard(type: CardData['type'] | undefined): boolean {
	return type !== undefined && CardFacade.hasFeature(type).answer;
}

function deltaSinceInit(now: number): number {
	return Math.round((now - INIT_TIME) / 1000);
}

/** Only VIEW_CARD and FINISH_STORY are stored for game time. */
const eventDeltas: Partial<Record<StoryEventType, number>> = {};

/** Total seconds spent on answerable cards so far (not including current segment if on one). */
let accumulatedGameTime = 0;
/** Timestamp (ms) when we entered the current/last answerable card; null when not on one. */
let lastGameCardEnterTime: number | null = null;
let isCurrentCardGameCard = false;

/**
 * Current game time: live on answerable card (real-time), else the later of VIEW_CARD or FINISH_STORY.
 */
export function getStoredEventDelta(): number {
	if (isCurrentCardGameCard && lastGameCardEnterTime !== null) {
		return Math.round(accumulatedGameTime + (Date.now() - lastGameCardEnterTime) / 1000);
	}
	return eventDeltas[StoryEventType.FINISH_STORY] ?? eventDeltas[StoryEventType.VIEW_CARD] ?? 0;
}

/**
 * Call at the very start of an event thunk. Returns delta to send;
 * only VIEW_CARD and FINISH_STORY update internal state.
 */
export function getDeltaForEvent(eventType: StoryEventType, cardType?: CardData['type']): number {
	const now = Date.now();

	switch (eventType) {
		case StoryEventType.VIEW_STORY:
			return deltaSinceInit(now);
		case StoryEventType.VIEW_CARD: {
			const enteringGameCard = isGameCard(cardType);
			if (lastGameCardEnterTime !== null) {
				accumulatedGameTime += (now - lastGameCardEnterTime) / 1000;
				lastGameCardEnterTime = null;
			}
			if (enteringGameCard) {
				lastGameCardEnterTime = now;
				isCurrentCardGameCard = true;
			} else {
				isCurrentCardGameCard = false;
			}
			const delta = Math.round(accumulatedGameTime);
			eventDeltas[StoryEventType.VIEW_CARD] = delta;
			return delta;
		}
		case StoryEventType.FINISH_STORY: {
			// Finish has no cardType; it always follows VIEW_CARD. Add open game segment if any.
			if (lastGameCardEnterTime !== null) {
				accumulatedGameTime += (now - lastGameCardEnterTime) / 1000;
				lastGameCardEnterTime = null;
				isCurrentCardGameCard = false;
			}
			const d = Math.round(accumulatedGameTime);
			eventDeltas[StoryEventType.FINISH_STORY] = d;
			return d;
		}
		case StoryEventType.START_STORY:
		case StoryEventType.REGISTER:
		case StoryEventType.STORY_SHARE:
			return getStoredEventDelta();
		default:
			return getStoredEventDelta();
	}
}
